import { useEffect, useState } from "react";
import { Pencil, Trash2, Loader2, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_LABEL = { conseil: "Conseil", faq: "FAQ", local: "Page locale" };

export default function ArticlesAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/articles").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const p = { ...form }; delete p.id; delete p.slug; delete p.created_at; delete p.updated_at;
      await api.put(`/admin/articles/${form.id}`, p);
      toast.success("Article enregistré"); setOpen(false); load();
    } catch { toast.error("Erreur"); } finally { setSaving(false); }
  };
  const togglePublish = async (a) => {
    const p = { ...a, published: !a.published }; delete p.id; delete p.slug; delete p.created_at; delete p.updated_at;
    await api.put(`/admin/articles/${a.id}`, p); load();
  };
  const remove = async (a) => { if (!window.confirm("Supprimer cet article ?")) return; await api.delete(`/admin/articles/${a.id}`); toast.success("Supprimé"); load(); };

  const field = "w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary";

  return (
    <div>
      <h1 className="font-heading text-3xl font-black text-ink">Articles & pages SEO</h1>
      <p className="text-ink/50 mt-1">Tous vos contenus publiés (conseils, FAQ, pages locales).</p>

      <div className="mt-6 bg-card border border-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left text-ink/60"><tr>
            <th className="px-4 py-3 font-semibold">Titre</th><th className="px-4 py-3 font-semibold hidden sm:table-cell">Type</th>
            <th className="px-4 py-3 font-semibold">Statut</th><th className="px-4 py-3 font-semibold text-right">Actions</th>
          </tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-t border-border" data-testid={`article-row-${a.id}`}>
                <td className="px-4 py-3 font-medium text-ink max-w-xs truncate">{a.title}</td>
                <td className="px-4 py-3 hidden sm:table-cell"><span className="text-xs bg-accent text-ink/70 px-2 py-1 rounded-full">{TYPE_LABEL[a.type] || a.type}</span></td>
                <td className="px-4 py-3"><button onClick={() => togglePublish(a)} className={`text-xs px-2 py-1 rounded-full ${a.published ? "bg-green-100 text-green-700" : "bg-muted text-ink/50"}`}>{a.published ? "Publié" : "Brouillon"}</button></td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Link to={`/conseils/${a.slug}`} target="_blank" className="p-2 text-ink/50 hover:text-primary"><ExternalLink size={15} /></Link>
                    <button onClick={() => { setForm(a); setOpen(true); }} className="p-2 text-ink/60 hover:text-primary"><Pencil size={15} /></button>
                    <button onClick={() => remove(a)} className="p-2 text-red-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!items.length && <p className="p-8 text-center text-ink/40">Aucun article. Utilisez le générateur SEO.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Modifier l'article</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><label className="text-xs font-semibold text-ink/60">Titre</label><input className={field} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-ink/60">Meta description</label><textarea rows={2} className={field} value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-ink/60">Extrait</label><textarea rows={2} className={field} value={form.excerpt || ""} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} /></div>
              <div><label className="text-xs font-semibold text-ink/60">Contenu (HTML)</label><textarea rows={12} className={`${field} font-mono text-xs`} value={form.content || ""} onChange={(e) => setForm({ ...form, content: e.target.value })} /></div>
              <button onClick={save} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={16} />} Enregistrer</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
